import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { PhoneNav } from "../../../shared/phone/PhoneNav";
import { PnRow, Segmented } from "../../../shared/phone/Row";
import { Split } from "../../../shared/phone/Split";
import { usePhone } from "../../../shared/phone/media";
import { Icon } from "../../components/ui/Icon";
import { ItemNative } from "./ItemNative";
import { LessonsNative } from "./LessonsNative";
import { CHANNELS, STAGES, labelOf, matches, type ShelfItem } from "./shelf";
import { useShelf } from "./useShelf";

/** What is open beside the list: an item, by any of its versions' ids, or Lessons. */
export type LibrarySel = { item: string } | "lessons" | null;

type Show = "skills" | "tools";

/** The list's view, search and place, kept while an iPad draws the list again beside each pick. */
const kept: { show: Show; q: string; top: number } = { show: "skills", q: "", top: 0 };

const holds = (it: ShelfItem, sel: LibrarySel) => !!sel && sel !== "lessons" && it.versions.some((v) => v.id === sel.item);

/** What an iPad opens beside the list at first: an update that waits, else the lessons that wait, else the first skill. */
function firstPick(items: ShelfItem[], waiting: number): LibrarySel {
  const waits = items.find((it) => it.waits);
  if (waits) return { item: waits.inUse.id };
  if (waiting) return "lessons";
  const first = CHANNELS.flatMap((c) => c.names).map((n) => items.find((it) => it.kind === "skill" && it.name === n))[0] ?? items[0];
  return first ? { item: first.inUse.id } : null;
}

/**
 * The Library on a phone and an iPad: the shelf as a list, and on an iPad the picked item or Lessons beside
 * it. `detail` is the route's own screen; on `/library` an iPad shows what waits for the owner.
 */
export function LibrarySplit({ sel, detail }: { sel: LibrarySel; detail: ReactNode | null }) {
  const phone = usePhone();
  const { items, waiting } = useShelf();
  // Why held: once the update beside the list is taken, the first pick would move on and close it mid-way.
  const held = useRef<LibrarySel>(null);
  if (!sel && !phone && items && !held.current) held.current = firstPick(items, waiting);
  const pick = sel ?? (phone ? null : held.current);
  const shown = detail ?? (pick === "lessons" ? <LessonsNative /> : pick ? <ItemNative id={pick.item} part={null} /> : null);
  return <Split list={<ShelfList sel={pick} />} detail={shown} showDetail={detail !== null} />;
}

const groups = (items: ShelfItem[], show: Show): { title: string; its: ShelfItem[] }[] => {
  const kind = show === "skills" ? "skill" : "method";
  const places = show === "skills" ? CHANNELS : STAGES;
  const out = places.map((g) => ({ title: g.title, its: g.names.flatMap((n) => items.filter((it) => it.kind === kind && it.name === n)) }));
  const other = items.filter((it) => it.kind === kind && !places.some((g) => g.names.includes(it.name)));
  if (other.length) out.push({ title: show === "skills" ? "Other" : "Other jobs", its: other });
  return out.filter((g) => g.its.length);
};

function Version({ it }: { it: ShelfItem }) {
  return (
    <span className="lb-ver">
      <span className={it.inUse.who === "you" ? "y" : undefined}>{labelOf(it.inUse)}</span>
      {it.waits ? <span className="w">v{it.waits.n} waits</span> : null}
    </span>
  );
}

/** The shelf as grouped rows: Lessons and what waits first, then skills by channel or tools by the stage of a goal. */
function ShelfList({ sel }: { sel: LibrarySel }) {
  const { items, waiting, err } = useShelf();
  const [show, setShow] = useState<Show>(kept.show);
  const [q, setQ] = useState(kept.q);
  const root = useRef<HTMLDivElement>(null);
  const picked = items?.find((it) => holds(it, sel)) ?? null;

  useEffect(() => {
    kept.show = show;
    kept.q = q;
  }, [show, q]);
  // An item opened from elsewhere shows its own half of the shelf.
  const pickedKind = picked?.kind;
  useEffect(() => {
    if (pickedKind) setShow(pickedKind === "skill" ? "skills" : "tools");
  }, [pickedKind]);
  // Why: on an iPad the list is drawn again beside each pick; it opens where the owner left it.
  const ready = items !== null;
  useLayoutEffect(() => {
    const pane = root.current?.closest<HTMLElement>(".pn-pane-list");
    if (!pane || !ready) return;
    pane.scrollTop = kept.top;
    const keep = () => {
      kept.top = pane.scrollTop;
    };
    pane.addEventListener("scroll", keep, { passive: true });
    return () => pane.removeEventListener("scroll", keep);
  }, [ready]);

  const all = items ?? [];
  const searching = !!q.trim();
  const found = all.filter((it) => matches(it, q));
  // A search looks through skills and tools alike, so a match is never hidden behind the other half.
  const shelf = searching ? [...groups(found, "skills"), ...groups(found, "tools")] : groups(found, show);
  const waits = all.filter((it) => it.waits);

  return (
    <div className="pn-screen" ref={root}>
      <PhoneNav title="Library" large />
      <div className="pn-stack">
        <div className={searching ? "lb-srch q" : "lb-srch"} role="search">
          <label className="pn-search">
            <Icon name="search" />
            <input
              type="search"
              placeholder="Find by name"
              aria-label="Find by name"
              autoComplete="off"
              enterKeyHint="search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()}
            />
          </label>
          <button
            type="button"
            className="lb-srch-x"
            onMouseDown={(e) => e.preventDefault()}
            onClick={(e) => {
              setQ("");
              e.currentTarget.parentElement?.querySelector("input")?.blur();
            }}
          >
            Cancel
          </button>
        </div>
        {err && !items ? (
          <div className="pn-empty">
            <b>The library did not load</b>
            {err}
          </div>
        ) : null}
        {items && !searching ? (
          <>
            <div className="pn-grp">
              <PnRow
                to="/library/lessons"
                icon={<Icon name="bulb" />}
                iconTone="warn"
                title="Lessons"
                sub={waiting ? `${waiting} ${waiting === 1 ? "waits" : "wait"} for your OK` : "What each goal learned"}
                value={waiting ? <span className="lb-cnt">{waiting}</span> : undefined}
                className={sel === "lessons" ? "on" : undefined}
              />
            </div>
            {waits.length > 0 && (
              <section>
                <div className="pn-cap">Waits for you</div>
                <div className="pn-grp">
                  {waits.map((it) => (
                    <PnRow
                      key={it.key}
                      to={`/library/${encodeURIComponent(it.inUse.id)}`}
                      icon={<Icon name="lbSparkle" />}
                      iconTone="warn"
                      title={`${it.title}: Flashadz v${it.waits?.n} is out`}
                      sub={it.waits?.note || undefined}
                      wrap
                      className={holds(it, sel) ? "on" : undefined}
                    />
                  ))}
                </div>
              </section>
            )}
          </>
        ) : null}
        {items && !searching ? (
          <Segmented<Show>
            label="Show"
            value={show}
            onChange={setShow}
            options={[
              { value: "skills", label: "Skills" },
              { value: "tools", label: "Tools" },
            ]}
          />
        ) : null}
        {shelf.map((g) => (
          <section key={g.title}>
            <div className="pn-cap">{g.title}</div>
            <div className="pn-grp lb-rows">
              {g.its.map((it) => (
                <PnRow
                  key={it.key}
                  to={`/library/${encodeURIComponent(it.inUse.id)}`}
                  icon={<Icon name={it.icon} />}
                  iconTone={it.waits ? "warn" : undefined}
                  title={it.title}
                  sub={it.line || undefined}
                  value={<Version it={it} />}
                  wrap
                  className={holds(it, sel) ? "on" : undefined}
                />
              ))}
            </div>
          </section>
        ))}
        {items && searching && !shelf.length ? (
          <div className="pn-empty">
            <b>Nothing matches</b>
            Try another name.
          </div>
        ) : null}
      </div>
    </div>
  );
}
